import Big from 'big.js';
import { Injectable } from '@angular/core';

Big.DP = 15;

@Injectable()
export class HelperManager {
  public formatDecimal(
    decimal: string,
    addDefaultDecimals?: boolean,
    precision?: number
  ): string {
    let transformedValue = decimal;

    if (precision === 0) {
      precision = 2;
    }
    if (precision && decimal.match(/,/g) && decimal.match(/,/g).length === 1) {
      decimal = decimal.replace(/\./g, '');
      // when number of decimals if lower then precision
      // -> don't do toFixed (strange behaviour e.g: 1234.256.toFixed(15) = 1234.256000000000085)
      // instead just at trailing zero's at the end of this function
      transformedValue = this.roundBigNumber(
        new Big(decimal.replace(',', '.')),
        precision
      ).toString();
    } else if (precision) {
      transformedValue = this.roundBigNumber(
        new Big(decimal),
        precision
      ).toString();
    }

    transformedValue = transformedValue
      .trim()
      .replace(/^(\-{0,1})(?:(?:0*)(\d+))?((?:\.)(?:\d*?))?0*$/g, '$1$2$3') // trim leading zero's, except if value after zero is not a number (e.g 0.15 should still be 0.15)
      .replace(/^(\-{0,1})(?:(?:0*)(\d+))?((?:\,)(?:\d*?))?0*$/g, '$1$2$3') // trim leading zero's, except if value after zero is not a number (e.g 0.15 should still be 0.15)
      .replace('.', ',') // replace decimal seperator '.' with ','
      .replace(/[,](?=.*[,])/g, ''); // replace all comma's except last one --> 5,000,50 should be 5000,50

    let transformedValueWithThousandsSeparator =
      transformedValue
        .substr(
          0,
          transformedValue.indexOf(',') > 0
            ? transformedValue.indexOf(',')
            : transformedValue.length
        ) // format thousands, but only part before comma
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.') +
      (transformedValue.indexOf(',') > 0
        ? transformedValue // add the decimal part again
            .substr(transformedValue.indexOf(','))
        : '');

    // if no decimal, or decimal character at end -> add default zero's)
    if (addDefaultDecimals) {
      transformedValueWithThousandsSeparator = this.addTrailingZeros(
        transformedValueWithThousandsSeparator,
        precision
      );
    }

    return transformedValueWithThousandsSeparator;
  }

  public formatDecimalToBig(number, noPrecision?: boolean): Big {
    if (!number) {
      return number;
    }
    if (noPrecision) {
      // first last point to comma
      let stringNumber = number
        ? number
            .toString()
            .replace(/\.(?![\s\S]*1\.)/, ',')
            .replace(/\./g, '')
            .replace(',', '.')
        : 0;
      if (noPrecision) {
        return Big(stringNumber);
      }
      // if decimal: last point to comma, then remove all thousands separators and finally transform , to decimal point
      if (number % 1 !== 0) {
        let decimalPrecision = 0;
        let stringValue = this.formatDecimal(
          number.toString(),
          true,
          decimalPrecision
        );

        return stringValue.indexOf(',') > 0
          ? this.parseBig(stringValue)
          : Big(stringValue);
      } else {
        // no decimal: remove all thousands separators and finally transform , to decimal point
        return number
          ? Big(number.toString().replace(/\./g, '').replace(',', '.'))
          : Big(0);
      }
    } else {
      return number
        ? number.toString().indexOf(',') > -1
          ? Big(number.toString().replace(/\./g, '').replace(',', '.'))
          : Big(number)
        : Big(0);
    }
  }

  addTrailingZeros(valueToTransform: string, precision): string {
    let returnValue = valueToTransform;

    if (
      precision > 0 &&
      (returnValue.indexOf(',') === -1 ||
        returnValue.indexOf(',') === returnValue.length - 1)
    ) {
      returnValue += returnValue.indexOf(',') === -1 ? ',' : '';
      while (returnValue.split(',')[1].length < precision) {
        returnValue += '0';
      }
    } else if (precision > 0 && returnValue.split(',')[1].length < precision) {
      while (returnValue.split(',')[1].length < precision) {
        returnValue += '0';
      }
    }

    return returnValue;
  }

  public formatNoDecimals(number: string) {
    let transformedValue = number
      .trim()
      .replace(/^(\-{0,1})(?:(?:0*)(\d+))?((?:\.)(?:\d*?))?0*$/g, '$1$2$3') // trim leading zero's, except if value after zero is not a number (e.g 0.15 should still be 0.15)
      .replace(/\./g, '') // replace decimal point character with ,
      .replace(/\,/g, '')
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

    return transformedValue;
  }

  public formatNoDecimalsNoThousandsSeparator(number: string) {
    return this.formatNoDecimals(number).replace(/\./g, '');
  }

  calculatePercentage(amount: number, totalAmount: number) {
    const percentage = (amount * 100) / totalAmount;
    const precision =
      this.getFirstNonZeroDecimalIndex(percentage.toString()) + 1;

    const roundedPercentageString = percentage.toFixed(Math.max(2, precision));
    return this.formatDecimal(
      roundedPercentageString,
      true,
      Math.max(2, precision)
    );
  }

  roundNumber(value: number, totalNumberOfDecimals: number) {
    // if value is e.g 0.000084256 it should return 0.000084
    // if value is 85.015986 it should return 85.02
    if (value < 0.01) {
      return this.toNonExponential(value.toPrecision(2));
    } else {
      return totalNumberOfDecimals
        ? new Big(value).toFixed(totalNumberOfDecimals).replace(/\.00$/, '')
        : value;
    }
  }

  roundBigNumber(value: Big, totalNumberOfDecimals: number): Big {
    if (!value) {
      return Big(0);
    }
    // if value is e.g 0.000084256 it should return 0.000084
    // if value is 85.015986 it should return 85.02
    // if (parseFloat(value.toString()) < 0.01) {
    //     return this.toNonExponential(value.toPrecision(2));
    // } else {
    return totalNumberOfDecimals
      ? Big(value.toFixed(totalNumberOfDecimals).replace(/\.00$/, ''))
      : value;
    // }
  }

  // remove scientific notation
  private toNonExponential(num) {
    //if the number is in scientific notation remove it
    if (/\d+\.?\d*e[\+\-]*\d+/i.test(num)) {
      let zero = '0',
        parts = String(num).toLowerCase().split('e'), //split into coeff and exponent
        e = parts.pop(), //store the exponential part
        l = Math.abs(+e), //get the number of zeros
        sign = +e / l,
        coeff_array = parts[0].split('.');
      if (sign === -1) {
        num = zero + '.' + new Array(l).join(zero) + coeff_array.join('');
      } else {
        let dec = coeff_array[1];
        if (dec) {
          l = l - dec.length;
        }
        num = coeff_array.join('') + new Array(l + 1).join(zero);
      }
    }

    return num;
  }

  countDecimals(value) {
    if (!value) {
      return;
    }
    const stringValue = value.toString();
    const decimalPoint = stringValue.indexOf(',') !== -1 ? ',' : '.';

    const split = stringValue.split(decimalPoint);
    // const defaultDecimals = 2;
    return split && split.length > 1
      ? stringValue.split(decimalPoint)[1].length
      : null;
  }

  // comma decimal to point decimal (original value)
  parse(value: string): number {
    if (value.indexOf(',') !== -1) {
      return +value.replace(/\./g, '').replace(/\,/g, '.');
    } else {
      return +value;
    }
  }

  parseBig(value: string): Big {
    if (value.indexOf(',') !== -1) {
      return Big(value.replace(/\./g, '').replace(/\,/g, '.'));
    } else {
      return Big(value);
    }
  }

  // Function to group a collection based on a property
  groupByAndCollect(collection: any[], keyFunction, collectFunction): any[] {
    const grouped = {};
    collection.map((obj) => {
      const id = keyFunction(obj);
      if (!grouped[id]) {
        grouped[id] = [];
      }
      grouped[id].push(obj);
    });
    return Object.keys(grouped).map((id) => collectFunction(id, grouped[id]));
  }

  // truncing without roundings: 10,567 -> 10,56
  truncateToDecimals(num) {
    // get meaningful precision: 10.562 -> precision = 2, 0.0000015 precision is 7
    const precision = this.getFirstNonZeroDecimalIndex(num.toString()) + 1;
    const calcDec = Math.pow(10, Math.max(precision, 2));

    return Math.trunc(num * calcDec) / calcDec;
  }

  getFirstNonZeroDecimalIndex(input: string): number {
    const split = input.split('.');
    if (split.length <= 1) {
      return 0;
    }
    return split[1].match('([1-9]+)').index;
  }

  multiplyBigDecimal(value1: number, value2: number): Big {
    if (value1 !== undefined && value2 !== undefined) {
      return new Big(value1).times(new Big(value2));
    }
  }

  subtractBigDecimal(value1: number, value2: number): Big {
    if (value1 !== undefined && value2 !== undefined) {
      return new Big(value1).minus(new Big(value2));
    }
  }

  sumBigDecimal(value1: number, value2: number, value3?: number): Big {
    if (value1 !== undefined && value2 !== undefined) {
      return new Big(value1)
        .plus(new Big(value2))
        .plus(value3 ? new Big(value3) : 0);
    }
  }
}
